import { Select } from "antd";
import type { CSSProperties } from "react";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import type { DataTableFilter } from "~components/DataTable/types/dataTableFilter.ts";
import type { FiltersConfig } from "~components/DataTable/types/filtersConfig.ts";
import SelectCompetition from "~components/SelectCompetition/SelectCompetition.tsx";
import SelectSeason from "~components/SelectSeason/SelectSeason.tsx";
import { TableFilter } from "~enums/tableFilter.ts";
import { ConductTableFilterTypes } from "~pages/Conducts/ConductListPage/enums/conductTableFilterTypes.ts";
import { useGetConductStatusOptions } from "~pages/Conducts/ConductListPage/hooks/useGetConductStatusOptions.ts";
import { useGetConductTypeOptions } from "~pages/Conducts/ConductListPage/hooks/useGetConductTypeOptions.ts";
import type { ConductFilter } from "~pages/Conducts/ConductListPage/types/conductFilter.ts";

const selectStyle: CSSProperties = { minWidth: 180 };

type ConductTableFilters = {
  filtersConfig: FiltersConfig;
  currentFilter: ConductFilter;
};

export const useGetConductTableFilters = (): ConductTableFilters => {
  const { formatMessage } = useIntl();
  const [currentFilter, setCurrentFilter] = useState<ConductFilter>({});
  const conductTypeOptions = useGetConductTypeOptions();
  const statusOptions = useGetConductStatusOptions();

  const handleFilterChange = useCallback((updatedFilter: ConductFilter) => {
    // Seasons are scoped to the selected competition, so a competition change drops the previously selected season
    setCurrentFilter((previousFilter) =>
      previousFilter.competition?.value === updatedFilter.competition?.value
        ? updatedFilter
        : { ...updatedFilter, [ConductTableFilterTypes.Season]: null },
    );
  }, []);

  const filters: Array<DataTableFilter> = [
    {
      filterKey: ConductTableFilterTypes.Competition,
      tableFilterType: TableFilter.Primary,
      filterElement: ({ value, onChange }) => (
        <SelectCompetition value={value} onChange={onChange} placeholder={formatMessage({ id: "competitionname" })} style={selectStyle} />
      ),
    },
    {
      filterKey: ConductTableFilterTypes.Season,
      tableFilterType: TableFilter.Primary,
      filterElement: ({ value, onChange }) => (
        <SelectSeason
          value={value}
          onChange={onChange}
          competitionId={currentFilter.competition?.value}
          disabled={!currentFilter.competition}
          placeholder={formatMessage({ id: "seasonname" })}
          style={selectStyle}
        />
      ),
    },
    {
      filterKey: ConductTableFilterTypes.ConductType,
      tableFilterType: TableFilter.Primary,
      filterElement: ({ value, onChange }) => (
        <Select
          mode="multiple"
          allowClear
          showSearch={false}
          maxTagCount={1}
          maxTagTextLength={10}
          options={conductTypeOptions}
          value={value}
          onChange={onChange}
          placeholder={formatMessage({ id: "conductType" })}
          style={selectStyle}
        />
      ),
    },
    {
      filterKey: ConductTableFilterTypes.Status,
      tableFilterType: TableFilter.Primary,
      filterElement: ({ value, onChange }) => (
        <Select
          allowClear
          showSearch={false}
          options={statusOptions}
          value={value}
          onChange={onChange}
          placeholder={formatMessage({ id: "status" })}
          style={selectStyle}
        />
      ),
    },
  ];

  return {
    filtersConfig: { filters, currentFilter, handleFilterChange },
    currentFilter,
  };
};
